import React from 'react'
import Observer from './Observer'

/**
 * An observer around a value
 * When the value is updated, the dependencies are notified
 */
export default class ValueObserver {
  value
  observer = new Observer()

  constructor(value) {
    this.value = value
  }

  notify(value) {
    this.observer.notify(value)
  }

  set(value) {
    value = value instanceof Function ? value(this.value) : value
    this.value = value
    this.notify(this.value)
  }

  subscribe(listener) {
    // listener(this.value)
    return this.observer.subscribe(listener)
  }

  /**
   * A hook to use in react components that declare a subscription to a given path
   * @param {Function} listener
   */
  useSubscribe(listener) {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(() => this.subscribe(listener), [])
  }

  /**
   * A hook to use in react components that return an always up to date value
   * @param {*} defaultValue
   */
  useValue(defaultValue) {
    // create local count state
    const [count, update] = React.useReducer(state => state + 1, 0)

    // subscribe to observable
    this.useSubscribe(update)

    return React.useMemo(
      () => (this.value === undefined ? defaultValue : this.value),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [count]
    )
  }

  /**
   * A hook to use in react components that provides the value setter
   * @param {*} defaultValue
   */
  useSetter() {
    return React.useCallback(value => this.set(value), [])
  }

  /**
   * A hook to use in react components that provides the value as a state (value + setter)
   * @param {*} defaultValue
   */
  useState(defaultValue) {
    return [this.useValue(defaultValue), this.useSetter()]
  }
}
